import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { StatCard } from '@/components/UI/StatCard';
import { Chip } from '@/components/UI/Chip';
import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import {
  useListWorkflowDefinitionsQuery,
  WorkflowStatus,
  WorkflowDefinitionSummary,
} from '@/redux/internal-services/workflowApi';

const STATUS_VARIANT: Record<WorkflowStatus, BadgeProps['variant']> = {
  draft:     'warning',
  published: 'success',
  archived:  'neutral',
};

const STATUSES: Array<{ label: string; value: WorkflowStatus | '' }> = [
  { label: 'All', value: '' },
  { label: 'Draft', value: 'draft' },
  { label: 'Published', value: 'published' },
  { label: 'Archived', value: 'archived' },
];

const StatusBadge: React.FC<{ status: WorkflowStatus }> = ({ status }) => (
  <Badge variant={STATUS_VARIANT[status]} className="capitalize">
    {status}
  </Badge>
);

const MetaPill: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <span className="inline-flex items-center gap-1 rounded-chip bg-ui-subtle px-2 py-0.5 text-xs text-ui-text2">
    <span className="font-medium">{value}</span>
    <span>{label}</span>
  </span>
);

const WorkflowsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<WorkflowStatus | ''>('');

  const { data, isLoading, isFetching } = useListWorkflowDefinitionsQuery(
    { status: statusFilter || undefined, limit: 100 },
    { refetchOnMountOrArgChange: true },
  );

  const definitions: WorkflowDefinitionSummary[] = data?.data ?? [];
  const total = data?.total ?? 0;

  const draftCount     = definitions.filter((d) => d.status === 'draft').length;
  const publishedCount = definitions.filter((d) => d.status === 'published').length;
  const archivedCount  = definitions.filter((d) => d.status === 'archived').length;

  return (
    <PageShell
      maxWidth="max-w-7xl"
      header={
        <PageHeader
          title="Workflow Definitions"
          subtitle="Manage platform-wide workflow definitions. Published definitions can be adopted by tenants."
          actions={
            <Button onClick={() => navigate('/platform/workflows/new')}>+ New Workflow</Button>
          }
          toolbar={
            <div className="flex flex-wrap gap-2">
              {STATUSES.map(({ label, value }) => (
                <Chip key={label} selected={statusFilter === value} onClick={() => setStatusFilter(value)}>
                  {label}
                </Chip>
              ))}
            </div>
          }
        />
      }
    >
      {/* Stats */}
      <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Total Definitions" value={isLoading ? '—' : total} tone="accent" />
        <StatCard label="Draft" value={isLoading ? '—' : draftCount} tone="warning" />
        <StatCard label="Published" value={isLoading ? '—' : publishedCount} tone="success" />
        <StatCard label="Archived" value={isLoading ? '—' : archivedCount} />
      </div>

      {/* Table */}
      <div className="table-card">
        {isLoading || isFetching ? (
          <LoadingState label="Loading workflow definitions..." />
        ) : definitions.length === 0 ? (
          <EmptyState
            title={t('platform.noWorkflowDefinitions')}
            description={t('platform.createFirstDefinition')}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="th-cell">Definition</th>
                  <th className="th-cell">Status</th>
                  <th className="th-cell hidden md:table-cell">Structure</th>
                  <th className="th-cell hidden lg:table-cell">Engagement Types</th>
                  <th className="th-cell hidden lg:table-cell">Updated</th>
                  <th className="th-cell text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {definitions.map((def) => (
                  <tr
                    key={`${def.definition_id}@${def.version}`}
                    className="tr-hover"
                    onClick={() => navigate(`/platform/workflows/${def.definition_id}/${def.version}`)}
                  >
                    <td className="td-cell">
                      <div>
                        <p className="text-sm font-medium text-ui-text">{def.name}</p>
                        <p className="text-xs text-ui-text3 mt-0.5 font-mono">{def.definition_id} v{def.version}</p>
                        {def.description && (
                          <p className="text-xs text-ui-text3 mt-0.5 line-clamp-1">{def.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="td-cell">
                      <StatusBadge status={def.status} />
                    </td>
                    <td className="td-cell hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        <MetaPill label="stages"  value={def.stage_count} />
                        <MetaPill label="statuses" value={def.status_count} />
                        <MetaPill label="transitions" value={def.transition_count} />
                      </div>
                    </td>
                    <td className="td-cell hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {def.engagement_types.length === 0 ? (
                          <span className="text-xs text-ui-text3">—</span>
                        ) : (
                          def.engagement_types.map((et) => (
                            <Badge key={et} variant="neutral" className="capitalize">
                              {et.replace(/_/g, ' ')}
                            </Badge>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="td-cell hidden lg:table-cell">
                      <span className="text-sm text-ui-text3">
                        {def.updated_on ? new Date(def.updated_on).toLocaleDateString() : '—'}
                      </span>
                    </td>
                    <td className="td-cell text-right">
                      <Button
                        variant="link"
                        size="xs"
                        className="text-accent-text"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/platform/workflows/${def.definition_id}/${def.version}`);
                        }}
                      >
                        View →
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default WorkflowsPage;
